/**
 * Helpers for the clinician Order & Kit Lookup page.
 *
 * These mirror the contract of `GET /secure/clinician/orders/search` so invalid
 * combinations are caught in the browser instead of coming back as a 400.
 */

export const ORDER_SEARCH_MIN_LIMIT = 1;
export const ORDER_SEARCH_MAX_LIMIT = 200;
export const ORDER_SEARCH_DEFAULT_LIMIT = 100;
export const ORDER_SEARCH_MAX_RANGE_DAYS = 730;
export const ORDER_SEARCH_DEFAULT_LOOKBACK_DAYS = 365;

export const EMPTY_VALUE = "—";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const trimToNull = (value) => {
  if (typeof value !== "string") {
    return value === null || value === undefined ? null : String(value).trim() || null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/** Fields that live behind the advanced-filter disclosure on the lookup page. */
export const ADVANCED_SEARCH_FIELDS = ["partnerId", "pharmacyPartner", "fromDate", "toDate", "limit"];

const invalid = (field, error) => ({ valid: false, error, field });

/**
 * Parses a `YYYY-MM-DD` value from a date input into a local Date, rejecting
 * calendar dates that do not exist (e.g. 2026-02-31 would otherwise roll over).
 */
const toLocalDate = (value, endOfDay = false) => {
  const trimmed = trimToNull(value);
  if (!trimmed) return null;

  const match = DATE_ONLY_PATTERN.exec(trimmed);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
};

/** Start of the selected local day as an ISO-8601 instant, or null. */
export const toIsoStartOfDay = (value) => {
  const date = toLocalDate(value, false);
  return date ? date.toISOString() : null;
};

/** End of the selected local day as an ISO-8601 instant, so the day is inclusive. */
export const toIsoEndOfDay = (value) => {
  const date = toLocalDate(value, true);
  return date ? date.toISOString() : null;
};

/**
 * The API only applies the date range when neither an order id nor a kit id is
 * given — exact identifier lookups bypass the scan entirely.
 */
export const isPartnerSearch = (filters = {}) =>
  !trimToNull(filters?.orderId) &&
  !trimToNull(filters?.kitId) &&
  Boolean(trimToNull(filters?.partnerId) || trimToNull(filters?.pharmacyPartner));

const normalizeLimit = (value) => {
  const trimmed = trimToNull(value);
  if (!trimmed) return ORDER_SEARCH_DEFAULT_LIMIT;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : NaN;
};

/**
 * Resolves the range the API would apply, matching its defaults: an absent
 * `toDate` means now, and an absent `fromDate` means the default lookback.
 */
const resolveRange = (fromDate, toDate) => {
  const to = toLocalDate(toDate, true) || new Date();
  const from =
    toLocalDate(fromDate, false) ||
    new Date(to.getTime() - ORDER_SEARCH_DEFAULT_LOOKBACK_DAYS * MS_PER_DAY);
  return { from, to };
};

/**
 * @param {object} filters
 * @returns {{ valid: boolean, error: string|null, field: string|null }} `field` names the
 * offending input so the caller can reveal it when it sits behind a disclosure.
 */
export const validateOrderSearchFilters = (filters = {}) => {
  const orderId = trimToNull(filters?.orderId);
  const kitId = trimToNull(filters?.kitId);
  const partnerId = trimToNull(filters?.partnerId);
  const pharmacyPartner = trimToNull(filters?.pharmacyPartner);

  if (!orderId && !kitId && !partnerId && !pharmacyPartner) {
    return invalid("orderId", "Enter an Order ID, Kit ID, Partner ID, or Pharmacy Partner to search.");
  }

  const limit = normalizeLimit(filters?.limit);
  if (!Number.isInteger(limit) || limit < ORDER_SEARCH_MIN_LIMIT || limit > ORDER_SEARCH_MAX_LIMIT) {
    return invalid(
      "limit",
      `Limit must be a whole number between ${ORDER_SEARCH_MIN_LIMIT} and ${ORDER_SEARCH_MAX_LIMIT}.`
    );
  }

  if (isPartnerSearch(filters)) {
    const rawFrom = trimToNull(filters?.fromDate);
    const rawTo = trimToNull(filters?.toDate);
    if (rawFrom && !toLocalDate(rawFrom)) {
      return invalid("fromDate", "From date must be a valid calendar date.");
    }
    if (rawTo && !toLocalDate(rawTo)) {
      return invalid("toDate", "To date must be a valid calendar date.");
    }

    const { from, to } = resolveRange(rawFrom, rawTo);
    if (from.getTime() > to.getTime()) {
      return invalid("fromDate", "From date must be on or before the To date.");
    }
    if (Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY) > ORDER_SEARCH_MAX_RANGE_DAYS) {
      return invalid("fromDate", `Partner searches are limited to ${ORDER_SEARCH_MAX_RANGE_DAYS} days.`);
    }
  }

  return { valid: true, error: null, field: null };
};

/**
 * Builds the query params for the search endpoint. Only non-empty filters are
 * included, and the date range is dropped for exact identifier lookups.
 *
 * @param {object} filters
 * @returns {Record<string, string>}
 */
export const buildOrderSearchParams = (filters = {}) => {
  const params = {};
  const orderId = trimToNull(filters?.orderId);
  const kitId = trimToNull(filters?.kitId);
  const partnerId = trimToNull(filters?.partnerId);
  const pharmacyPartner = trimToNull(filters?.pharmacyPartner);

  if (orderId) params.orderId = orderId;
  if (kitId) params.kitId = kitId;
  if (partnerId) params.partnerId = partnerId;
  if (pharmacyPartner) params.pharmacyPartner = pharmacyPartner;

  if (isPartnerSearch(filters)) {
    const fromIso = toIsoStartOfDay(filters?.fromDate);
    const toIso = toIsoEndOfDay(filters?.toDate);
    if (fromIso) params.fromDate = fromIso;
    if (toIso) params.toDate = toIso;
  }

  const limit = normalizeLimit(filters?.limit);
  params.limit = String(Number.isInteger(limit) ? limit : ORDER_SEARCH_DEFAULT_LIMIT);
  return params;
};

/** Formats an API timestamp for display, falling back to an em dash. */
export const formatDateTime = (value) => {
  const trimmed = trimToNull(value);
  if (!trimmed) return EMPTY_VALUE;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Formats an API date-only value (e.g. testTakenDate) for display. */
export const formatDate = (value) => {
  const trimmed = trimToNull(value);
  if (!trimmed) return EMPTY_VALUE;
  const localDate = toLocalDate(trimmed);
  const date = localDate || new Date(trimmed);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

/** Displays a value, or an em dash when the API omitted it. */
export const displayValue = (value) => {
  const trimmed = trimToNull(value);
  return trimmed === null ? EMPTY_VALUE : trimmed;
};
